import React, { createContext } from 'react'
import { useQuery } from '@apollo/client'
import { NotificationInline } from 'oio-react'
import PropTypes from 'prop-types'
import uiConstants from 'config/constants/ui'
import { getCurrentOrganization } from 'src/graphql/queries/getCurrentOrganization.gql'

export const OrganizationContext = createContext()

const OrganizationProvider = ({ children }) => {
   const { data, error, loading, ...otherQueryReturnValues } = useQuery(getCurrentOrganization, {
      errorPolicy: 'all',
      fetchPolicy: 'cache-first',
      pollInterval: process.env.NODE_ENV !== 'test'
         ? 60000
         : undefined
   })

   // Ensure organization is loaded before rendering
   if (loading) {
      return null
   }

   const hasFatalErrors = error?.networkError ||
      error?.graphQLErrors?.some(err => err?.extensions?.exception?.name !== 'NonFatal')

   // If there was a fatal issue and no data has loaded yet, then there's
   // nothing we can do, an error must be thrown.
   if (hasFatalErrors && !data?.currentOrganization) {
      throw error
   }

   const contextValue = {
      organization: data?.currentOrganization,
      error,
      loading,
      ...otherQueryReturnValues
   }

   return (
      <OrganizationContext.Provider value={contextValue}>
         {children}

         {/* If a fatal error was encountered, but we have organization data,
            then we try to render */}
         {/* TODO: It would be better to use `showNotification`, but the NotificationManager
            context will force a re-render on all children components, which will create a
            circular loop and the page will blow up. This needs to be fixed in OIO.
            See: https://github.com/mother/oio-react/issues/78
         */}
         {hasFatalErrors && (
            <div style={{
               zIndex: uiConstants.zIndexes.orgContextFatalErrorMessage,
               position: 'fixed',
               bottom: '80px',
               right: '20px'
            }}>
               <NotificationInline
                  backgroundColor="rgba(40,40,40,0.95)"
                  borderRadius="8px"
                  message={error.message}
                  iconSpacing="24px"
                  paddingHorizontal="24px"
                  paddingVertical="24px"
                  textColor="#fff"
                  title="Error"
                  type="error"
               />
            </div>
         )}
      </OrganizationContext.Provider>
   )
}

OrganizationProvider.propTypes = {
   children: PropTypes.node.isRequired
}

export { OrganizationProvider }
